// src/components/Dashboard/ClaimsDirectory.jsx
// Vista de solo lectura de TODOS los reclamos, agrupados por empresa y ajustador.
// No importa updateClaim/deleteClaim/markAsPaid a propósito: este componente
// nunca debe poder escribir en Firestore, solo leer y mostrar.
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LanguageContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import { loadDirectoryChecks, loadDirectoryRecords } from '../../services/directoryData';
import { getAdjusters } from '../../services/adjusterService';
import { formatDateToMMDDYYYY, parseDateForSort } from '../../utils/dateHelpers';
import { proposeCheckStep } from '../../migration/flowMigration';
import { claimStepLabel, getStep } from '../../services/flowService';
import { TimerBadge } from './FlowBits';
import { CHECK_FLOW, CLAIM_FLOW, CLAIM_PHASES, FINAL_STEPS } from '../../config/claimFlow';
import ClaimRecordList from './ClaimRecordList';
import useUrlParams from '../../hooks/useUrlParams';
import { matchesStepFilter } from '../../utils/claimHelpers';
import { rememberListPosition, peekListPosition, clearListPosition, scrollToListPosition } from '../../utils/listReturn';
import './ClaimsDirectory.css';
import { paymentBackupStatus, PAYMENT_BACKUP_LABELS } from '../../utils/paymentBackup';
import { rdAlerts } from '../../utils/recoverableDepreciation';
import { awaitingPaItems, deadlineItems, daysInStep } from '../../utils/claimAttention';
import AttentionBoard from './AttentionBoard';
import PaCards from './PaCards';
import CompanyLogos from '../CompanyLogos';

// Días en el mismo paso a partir de los cuales un reclamo cuenta como detenido.
export const STUCK_DAYS = 14;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0);
};

// Estado de un cheque = el paso en el que está (CHECK_FLOW). Los cheques
// anteriores al flujo no tienen paso guardado: se deduce el equivalente con
// la misma regla de la migración (proposeCheckStep).
const checkStage = (check, language) => {
  const stepId = check.flow?.stepId || proposeCheckStep(check);
  const step = getStep('check', stepId);
  const done = stepId === 'check_archived';
  return { key: stepId, label: step ? step.label[language === 'es' ? 'es' : 'en'] : stepId, className: done ? 'cd-status-paid' : 'cd-status-pending' };
};

// Datos del reclamo expandido en Comisiones: solo Cliente y Reclamo (póliza,
// aseguradora y pago viven en la ficha del reclamo). Solo filas con valor.
const joinName = person => [person?.firstName, person?.lastName].filter(Boolean).join(' ');
const contactLine = (...parts) => parts.filter(Boolean).join(' · ');
export const claimSections = (record = {}, primary = {}, language = 'es') => {
  const es = language === 'es';
  const pick = key => record[key] ?? primary[key];
  const date = value => (value ? formatDateToMMDDYYYY(value) : '');
  const mailing = [record.mailingAddressStreet, record.mailingAddressCity, record.mailingAddressState, record.mailingAddressZip].filter(Boolean).join(', ');
  const sections = [
    { title: es ? 'Cliente' : 'Client', icon: 'fa-solid fa-user', rows: [
      [es ? 'Nombre' : 'Name', pick('clientName')],
      [es ? 'Co-propietario' : 'Co-owner', joinName(record.owner2)],
      [es ? 'Correo' : 'Email', pick('clientEmail')],
      [es ? 'Teléfono' : 'Phone', pick('clientPhone')],
      [es ? 'Móvil' : 'Mobile', record.clientMobile],
      [es ? 'Propiedad' : 'Property', pick('clientAddress') || record.propertyAddress],
      [es ? 'Dirección postal' : 'Mailing address', mailing]
    ] },
    { title: es ? 'Reclamo' : 'Claim', icon: 'fa-solid fa-file-shield', rows: [
      ['Claim #', pick('claimNumber')],
      [es ? 'Tipo' : 'Type', record.claimType],
      [es ? 'Causa' : 'Cause', pick('cause')],
      [es ? 'Fecha de pérdida' : 'Date of loss', contactLine(date(record.incidentDate), record.lossTime)],
      [es ? 'Descripción' : 'Description', record.lossDescription],
      [es ? 'Ajustador' : 'Adjuster', pick('adjusterName')],
      [es ? 'Contrato firmado' : 'Contract signed', date(record.contractSignedDate)],
      [es ? 'Abogado' : 'Attorney', record.attorneyName]
    ] },
  ];
  return sections
    .map(section => ({ ...section, rows: section.rows.filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '') }))
    .filter(section => section.rows.length > 0);
};

// Nombre para emparejar el P.A del cheque con su perfil (foto) sin fallar por
// mayúsculas o espacios de más.
const normName = name => String(name || '').trim().replace(/\s+/g, ' ').toLowerCase();

// Filtro por paso del cheque: tramo A (cheque del seguro) y B (cobro CSR).
const STATUS_FILTER_OPTIONS = [
  { key: 'all', es: 'Todos los pasos del cheque', en: 'All check steps' },
  ...CHECK_FLOW.steps.map(step => ({ key: step.id, es: step.label.es, en: step.label.en }))
];

const SORTERS = {
  invoiceNumber: (c) => {
    const n = Number(String(c.invoiceNumber || '').replace(/^#/, ''));
    return Number.isFinite(n) ? n : (c.invoiceNumber || '');
  },
  claimNumber: (c) => c.claimNumber || '',
  clientName: (c) => (c.clientName || '').toLowerCase(),
  companyName: (c) => (c.companyName || '').toLowerCase(),
  adjusterName: (c) => (c.adjusterName || '').toLowerCase(),
  checkDate: (c) => parseDateForSort(c.checkDate),
  amount: (c) => c.amount || 0,
  totalCollected: (c) => c.totalCollected || 0,
  pending: (c) => c.pending || 0
};

// Última compañía elegida en el selector de arriba (comodidad por navegador).
const COMPANY_SCOPE_KEY = 'csr.claims.companyScope';

// lockedCompany: si se define (rol 'adjuster'), la consulta a Firestore se
// limita a esa empresa (getClaimsByCompany) y el filtro de empresa queda fijo
// — esto es lo que hace que firestore.rules pueda autorizar la lectura, ya
// que la regla exige que el query traiga un where('companyName', '==', ...).
// view: 'claims' | 'commissions' fija la vista (pestañas propias del menú);
// sin view se muestra el selector Reclamos / Comisiones de antes.
const ClaimsDirectory = ({ lockedCompany = null, lockedAdjuster = null, view = null }) => {
  const { language } = useContext(LanguageContext);
  const { isAdmin, can, userRoleKey, userCompanies } = useAuth();
  // CSR Assistant: varias compañías, y lee el espejo de cheques (sin lo que gana CSR).
  const csrMode = userRoleKey === 'csr_assistant';
  // Quien ve decisiones de OTROS P.A (equipo CSR o P.A Admin): "Esperando
  // decisión del PA", plegado. El P.A ve "Tus decisiones pendientes", abierto.
  const csrTeam = isAdmin || csrMode || userRoleKey === 'pa_admin';
  // Check commissions (lo que gana CSR): Super Admin y P.A Admin.
  const showCommissions = isAdmin || can('viewCheckCommissions');
  // El monto de CSR (su %, lo cobrado y lo pendiente) solo lo ven Super Admin y P.A Admin.
  const showCsrAmounts = isAdmin || can('viewEarnings');
  // Todo lo cargado; `claims` y `claimRecords` (más abajo) son lo de la compañía elegida.
  const [allChecks, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Pantalla en dos pestañas: 'claims' = un reclamo por tarjeta con su paso
  // (ClaimRecordList, la principal); 'commissions' = la lista de cheques de
  // siempre, para el cobro de comisiones.
  // La pestaña vive en la URL (?dir=commissions) para volver a la misma.
  const location = useLocation();
  const [urlParams, setUrlParams] = useUrlParams({ dir: 'claims', co: 'all', cq: '', cpa: 'all', cst: 'all', cstep: 'all', cview: 'all', csort: 'invoiceNumber', cdir: 'desc' });
  const directoryTab = view ? (view === 'commissions' && showCommissions ? 'commissions' : 'claims')
    : urlParams.dir === 'commissions' && showCommissions ? 'commissions' : 'claims';
  const setDirectoryTab = tab => setUrlParams({ dir: tab });
  const [recordsLoaded, setRecordsLoaded] = useState(false);
  const [commissionFiltersOpen, setCommissionFiltersOpen] = useState(false);

  // Búsqueda, filtros, orden y vista de "Comisiones por cheque" también viven
  // en la URL (parámetros c*), para recuperarlos al volver de una ficha.
  const searchTerm = urlParams.cq;
  const setSearchTerm = value => setUrlParams({ cq: value });
  // La compañía se elige arriba (selector visible) y separa toda la pantalla:
  // avisos, lista de reclamos y comisiones. Los datos ya llegan filtrados.
  const companyFilter = lockedCompany || 'all';
  const adjusterFilter = lockedAdjuster || urlParams.cpa;
  const setAdjusterFilter = value => setUrlParams({ cpa: value });
  // Filtro por etapa real del cheque (5 opciones) — reemplaza el antiguo
  // paid/pending binario, portado de MyClaims.jsx (ver STATUS_FILTER_OPTIONS
  // arriba y docs/archivo/REFACTOR_MYCLAIMS_CLAIMSTATUS.md).
  const statusFilter = urlParams.cst;
  const setStatusFilter = value => setUrlParams({ cst: value });
  // Filtro por paso del reclamo (flujo de pasos): 'all' | 'phase:N' | stepId | 'none'
  const stepFilter = urlParams.cstep;
  const setStepFilter = value => setUrlParams({ cstep: value });
  const [allClaimRecords, setClaimRecords] = useState([]);
  // Compañías con reclamos o cheques, para el selector de arriba (solo si hay más de una).
  const companyOptions = useMemo(() => {
    const counts = new Map();
    allClaimRecords.forEach(cr => { if (cr.companyName) counts.set(cr.companyName, (counts.get(cr.companyName) || 0) + 1); });
    allChecks.forEach(check => { if (check.companyName && !counts.has(check.companyName)) counts.set(check.companyName, 0); });
    return Array.from(counts, ([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allClaimRecords, allChecks]);
  const showCompanyPicker = !lockedCompany && companyOptions.length > 1;
  const companyScope = showCompanyPicker && companyOptions.some(c => c.name === urlParams.co) ? urlParams.co : 'all';
  const setCompanyScope = value => {
    setUrlParams({ co: value, cpa: 'all', pa: undefined });
    try { localStorage.setItem(COMPANY_SCOPE_KEY, value); } catch { /* sin almacenamiento: solo queda en la URL */ }
  };
  // Al entrar sin compañía en la URL se recupera la última elegida.
  useEffect(() => {
    if (lockedCompany || urlParams.co !== 'all') return;
    let saved = '';
    try { saved = localStorage.getItem(COMPANY_SCOPE_KEY) || ''; } catch { /* sin almacenamiento */ }
    if (saved && saved !== 'all') setUrlParams({ co: saved });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const claims = useMemo(() => (companyScope === 'all' ? allChecks : allChecks.filter(c => c.companyName === companyScope)), [allChecks, companyScope]);
  const claimRecords = useMemo(() => (companyScope === 'all' ? allClaimRecords : allClaimRecords.filter(cr => cr.companyName === companyScope)), [allClaimRecords, companyScope]);
  // El P.A Admin ve siempre las tarjetas de sus P.A arriba, así que no usa la vista agrupada.
  const paAdmin = userRoleKey === 'pa_admin';
  // Super Admin y P.A Admin ven siempre las tarjetas de P.A (con foto) arriba,
  // así que no necesitan la vista agrupada.
  const alwaysPaCards = (paAdmin || isAdmin) && !lockedAdjuster;
  const viewMode = !alwaysPaCards && urlParams.cview === 'grouped' ? 'grouped' : 'all'; // all | grouped
  const setViewMode = value => setUrlParams({ cview: value });
  const [adjusters, setAdjusters] = useState([]);
  const sortField = urlParams.csort;
  const setSortField = value => setUrlParams({ csort: value });
  const sortDirection = urlParams.cdir === 'asc' ? 'asc' : 'desc';
  const setSortDirection = value => setUrlParams({ cdir: typeof value === 'function' ? value(sortDirection) : value });
  const statusFilterLabel = (key) => {
    const opt = STATUS_FILTER_OPTIONS.find(o => o.key === key);
    return opt ? (language === 'es' ? opt.es : opt.en) : key;
  };
  const stepFilterLabel = key => {
    if (key === 'none') return language === 'es' ? 'Sin paso asignado' : 'No step';
    if (key.startsWith('phase:')) {
      const phase = CLAIM_PHASES.find(p => `phase:${p.id}` === key);
      return phase ? `${language === 'es' ? 'Fase' : 'Phase'} ${phase.id}: ${language === 'es' ? phase.label.es : phase.label.en}` : key;
    }
    const step = CLAIM_FLOW.steps.find(st => st.id === key);
    return step ? (language === 'es' ? step.label.es : step.label.en) : key;
  };
  const activeFilters = [
    searchTerm.trim() && { label: searchTerm.trim(), clear: () => setSearchTerm('') },
    stepFilter !== 'all' && { label: stepFilterLabel(stepFilter), clear: () => setStepFilter('all') },
    statusFilter !== 'all' && { label: statusFilterLabel(statusFilter), clear: () => setStatusFilter('all') },
    !lockedAdjuster && adjusterFilter !== 'all' && { label: adjusterFilter, clear: () => setAdjusterFilter('all') }
  ].filter(Boolean);
  const clearFilters = () => setUrlParams({ cq: '', cst: 'all', cstep: 'all', cpa: 'all' });

  // Tarjetas colapsadas por defecto (mobile): solo Factura/Estado, Cliente
  // y Monto visibles hasta que se toca la tarjeta. Un solo Set cubre tanto
  // la vista "Todos los Reclamos" (tabla) como "Por Ajustador" (grid de
  // tarjetas), ya que claim.id es único en ambas.
  const [expandedIds, setExpandedIds] = useState(new Set());
  // Al abrir, la tarjeta ocupa toda la fila (CSS .cd-card-expanded) y la
  // página baja hasta ella; las demás se reacomodan alrededor.
  const toggleExpand = (claimId) => {
    const opening = !expandedIds.has(claimId);
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(claimId)) next.delete(claimId); else next.add(claimId);
      return next;
    });
    if (opening) {
      requestAnimationFrame(() => {
        const card = document.querySelector(`[data-group-key="${window.CSS?.escape ? window.CSS.escape(String(claimId)) : String(claimId)}"]`);
        card?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  // Cerrar todas las tarjetas expandidas al cambiar de vista (Todos los
  // Reclamos <-> Por Ajustador) — evita arrastrar tarjetas abiertas de una
  // pantalla a otra.
  useEffect(() => {
    setExpandedIds(new Set());
  }, [viewMode]);

  // Al volver de una ficha: reabre las tarjetas que estaban expandidas y
  // recupera la posición, una vez cargados los cheques.
  const restoredPosition = useRef(false);
  useEffect(() => {
    if (restoredPosition.current || loading || directoryTab !== 'commissions') return undefined;
    const saved = peekListPosition(location.search);
    if (!saved || saved.tab !== 'commissions') return undefined;
    restoredPosition.current = true;
    clearListPosition();
    setExpandedIds(new Set(saved.expanded || []));
    let inner;
    const outer = requestAnimationFrame(() => { inner = requestAnimationFrame(() => scrollToListPosition(saved)); });
    return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, directoryTab]);

  useEffect(() => {
    const loadClaims = async () => {
      setLoading(true);
      setError('');
      try {
        setClaims(await loadDirectoryChecks({ csrMode, userCompanies, lockedCompany, lockedAdjuster }));
      } catch (err) {
        console.error('Error loading claims directory:', err);
        setError(language === 'es' ? 'Error al cargar los reclamos' : 'Error loading claims');
      }
      setLoading(false);
    };
    loadClaims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedCompany, lockedAdjuster]);

  useEffect(() => {
    getAdjusters().then(setAdjusters);
  }, []);

  useEffect(() => {
    if (loading) return;
    const loadPending = async () => {
      try {
        setClaimRecords(await loadDirectoryRecords({ csrMode, userCompanies, lockedCompany, lockedAdjuster }));
      } catch (err) {
        console.error('Error loading claim records:', err);
      } finally {
        setRecordsLoaded(true);
      }
    };
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChecks, loading, lockedCompany, lockedAdjuster]);

  const profileByName = useMemo(() => {
    const map = new Map();
    adjusters.forEach(a => { if (a.name) map.set(normName(a.name), a); });
    return map;
  }, [adjusters]);

  // Paso del reclamo (flujo de pasos) de cada cheque, vía su claimRecord.
  const recordsById = useMemo(() => new Map(claimRecords.map(cr => [cr.id, cr])), [claimRecords]);
  const matchesStep = record => matchesStepFilter(record, stepFilter);

  // Decisiones pendientes del PA y plazos que vencen (utils/claimAttention.js,
  // compartido con el inicio del ajustador).
  const awaitingPa = useMemo(() => awaitingPaItems(claimRecords, claims, language), [claimRecords, claims, language]);
  // Cheques cobrados sin comprobante (BofA / Zelle): solo para quien ve el dinero de CSR.
  const paymentBacklog = showCsrAmounts
    ? claims.map(check => ({ check, status: paymentBackupStatus(check) })).filter(item => item.status === 'pending_backup')
    : [];
  const deadlineAlerts = useMemo(() => deadlineItems(claimRecords, claims, language), [claimRecords, claims, language]);

  // Depreciación recuperable lista para pedir: el reclamo tiene RD y el
  // cliente ya cobró, pero todavía no se pidió (utils/recoverableDepreciation.js).
  const rdDue = useMemo(() => rdAlerts(claimRecords, claims), [claimRecords, claims]);

  const daysIn = cr => daysInStep(cr);

  // Reclamos detenidos: llevan STUCK_DAYS días o más en el mismo paso (sin
  // contar los terminados ni los que ya salen en "Plazos que vencen"). Los
  // migrados sin fecha de entrada al paso no se pueden medir y no aparecen.
  const stuckClaims = useMemo(() => {
    const inDeadlines = new Set(deadlineAlerts.map(alert => alert.key));
    return claimRecords
      .filter(cr => cr.flow?.stepId && !FINAL_STEPS.includes(cr.flow.stepId) && !inDeadlines.has(`claim-${cr.id}`))
      .map(cr => ({ cr, days: daysIn(cr) }))
      .filter(item => item.days !== null && item.days >= STUCK_DAYS)
      .sort((a, b) => b.days - a.days);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- daysIn solo lee cr.flow
  }, [claimRecords, deadlineAlerts]);


  const filteredClaims = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return claims.filter(claim => {
      if (companyFilter !== 'all' && claim.companyName !== companyFilter) return false;
      if (adjusterFilter !== 'all' && claim.adjusterName !== adjusterFilter) return false;
      if (statusFilter !== 'all' && checkStage(claim, language).key !== statusFilter) return false;
      if (stepFilter !== 'all' && !matchesStep(recordsById.get(claim.claimRecordId))) return false;
      if (term) {
        const haystack = [
          claim.clientName, claim.claimNumber, claim.invoiceNumber,
          claim.adjusterName, claim.companyName
        ].join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, companyFilter, adjusterFilter, statusFilter, stepFilter, recordsById, searchTerm, language]);

  // Reclamos para la fila de botones de ajustador en "Por Ajustador":
  // respeta todos los filtros MENOS adjusterFilter, para que seleccionar
  // un ajustador no haga desaparecer a los demás botones.
  const claimsForAdjusterButtons = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return claims.filter(claim => {
      if (companyFilter !== 'all' && claim.companyName !== companyFilter) return false;
      if (statusFilter !== 'all' && checkStage(claim, language).key !== statusFilter) return false;
      if (stepFilter !== 'all' && !matchesStep(recordsById.get(claim.claimRecordId))) return false;
      if (term) {
        const haystack = [
          claim.clientName, claim.claimNumber, claim.invoiceNumber,
          claim.adjusterName, claim.companyName
        ].join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, companyFilter, statusFilter, stepFilter, recordsById, searchTerm, language]);

  const adjustersInView = useMemo(() => {
    // Esta vista es por cheque: el número es de cheques, y aparte cuántos
    // reclamos distintos tienen esos cheques (los reclamos totales del PA
    // están en la lista de Reclamos y en el Resumen).
    const counts = new Map();
    const claimIds = new Map();
    const amounts = new Map();
    claimsForAdjusterButtons.forEach(claim => {
      const name = claim.adjusterName || (language === 'es' ? 'Sin ajustador' : 'No adjuster');
      counts.set(name, (counts.get(name) || 0) + 1);
      amounts.set(name, (amounts.get(name) || 0) + (Number(claim.amount) || 0));
      if (!claimIds.has(name)) claimIds.set(name, new Set());
      claimIds.get(name).add(claim.claimRecordId || claim.id);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count, claimCount: claimIds.get(name).size, amount: amounts.get(name) || 0 }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [claimsForAdjusterButtons, language]);


  // Agrupa los cheques (checks) por reclamo real (claimRecordId — o
  // claimNumber para cheques legado sin ese campo) para que un ajustador
  // vea un solo bloque por caso en vez de una tarjeta repetida por cada
  // cheque, misma lógica que ya usa MyClaims.jsx del lado admin. Los
  // cheques adentro del grupo se ordenan con el mismo criterio que la
  // lista general; el grupo en sí se ordena por su primer cheque ya
  // ordenado (ej. con "N.º de creación" desc, el grupo sube por su
  // invoice más alto — mismo criterio que maxInvoiceNumber en MyClaims.jsx).
  const claimGroups = useMemo(() => {
    const getter = SORTERS[sortField] || SORTERS.checkDate;
    const groupsByKey = new Map();
    filteredClaims.forEach(claim => {
      const key = claim.claimRecordId || claim.claimNumber || claim.id;
      if (!groupsByKey.has(key)) groupsByKey.set(key, []);
      groupsByKey.get(key).push(claim);
    });
    const compare = (a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (sortField === 'checkDate') {
        if (va === 0 && vb !== 0) return 1;
        if (vb === 0 && va !== 0) return -1;
      }
      if (va < vb) return sortDirection === 'asc' ? -1 : 1;
      if (va > vb) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    };
    const groups = Array.from(groupsByKey.values()).map(checks => [...checks].sort(compare));
    groups.sort((a, b) => compare(a[0], b[0]));
    return groups;
  }, [filteredClaims, sortField, sortDirection]);

  // Un solo cheque o varios: misma estructura y mismo comportamiento de
  // colapso SIEMPRE — el resumen (cliente/empresa/ajustador/aseguradora/
  // correo/teléfono/causa) va arriba y la lista de cheques colapsa/expande
  // con el mismo toggle en ambos casos. Antes un reclamo de un solo cheque
  // quedaba siempre "abierto" mientras los de varios arrancaban cerrados —
  // al lado uno del otro parecía que a los de varios cheques les faltaba
  // información (ver feedback). Único diferencial real entre uno y varios:
  // el badge (estado vs. cantidad de cheques) y el texto Cheque/Total.
  const renderClaimGroup = (checks, options = {}) => {
    const primary = checks[0];
    const isSingle = checks.length === 1;
    const groupKey = primary.claimRecordId || primary.claimNumber || primary.id;
    const isGroupExpanded = expandedIds.has(groupKey);
    const totalAmount = checks.reduce((sum, c) => sum + (c.amount || 0), 0);
    const totalEarning = checks.reduce((sum, c) => {
      const gross = ((c.amount || 0) * (c.adjusterPercentage || 0)) / 100;
      return sum + (gross - (c.totalCollected || 0));
    }, 0);
    const headerToggle = () => toggleExpand(groupKey);
    const es = language === 'es';
    const record = recordsById.get(primary.claimRecordId) || {};
    const stepLabel = claimStepLabel(record, language) || (es ? 'Sin paso asignado' : 'No step yet');
    const openClaim = (
      <Link to={`/claim-status/claim/${groupKey}`} state={{ from: `${location.pathname}${location.search}` }} data-claim-id={groupKey} className="cdx-open"
        onClick={event => { event.stopPropagation(); rememberListPosition(location.search, groupKey, { tab: 'commissions', expanded: [...expandedIds] }); }}>
        <i className="fa-solid fa-folder-open" aria-hidden="true" /> {es ? 'Abrir el reclamo' : 'Open the claim'}
      </Link>
    );

    if (isGroupExpanded) {
      const csrOwed = checks.reduce((sum, c) => sum + (Number(c.totalCollected) || 0), 0);
      const csrPaid = checks.reduce((sum, c) => sum + (Number(c.deposit) || 0), 0);
      const csrPending = checks.reduce((sum, c) => sum + (Number(c.pending) || 0), 0);
      const paidChecks = checks.filter(c => checkStage(c, language).className === 'cd-status-paid').length;
      const sections = claimSections(record, primary, language);
      return (
        <div key={groupKey} data-group-key={groupKey} className="cd-claim-card cd-claim-group cd-card-expanded">
          <header className="cdx-head">
            <div className="cdx-title">
              <span className="cdx-ids">
                <span className="cdx-claim-id"><i className="fa-solid fa-hashtag" aria-hidden="true" /> Claim {primary.claimNumber || '—'}</span>
                {record.policyNumber && <span className="cdx-policy-id">{es ? 'Póliza' : 'Policy'} {record.policyNumber}</span>}
              </span>
              <h3>{primary.clientName || 'N/A'}</h3>
              <p>{[primary.insuranceCompanyName, primary.adjusterName, options.showCompany && primary.companyName].filter(Boolean).join(' · ')}</p>
              <span className="cdx-step"><i className="fa-solid fa-route" aria-hidden="true" /> {stepLabel}</span>
            </div>
            <dl className="cdx-kpis">
              <div><dt>{es ? 'Cheques' : 'Checks'}</dt><dd>{formatCurrency(totalAmount)}</dd><span>{es ? `${checks.length} cheque(s) · ${paidChecks} cobrado(s)` : `${checks.length} check(s) · ${paidChecks} collected`}</span></div>
              <div><dt>{es ? 'Neto del PA' : 'PA net'}</dt><dd>{formatCurrency(totalEarning)}</dd><span>{es ? 'Honorarios − CSR' : 'Fees − CSR'}</span></div>
              {showCsrAmounts && <div><dt>CSR</dt><dd>{formatCurrency(csrOwed)}</dd><span>{es ? `Cobrado ${formatCurrency(csrPaid)}` : `Collected ${formatCurrency(csrPaid)}`}</span></div>}
              {showCsrAmounts && <div className={csrPending > 0 ? 'is-pending' : ''}><dt>{es ? 'CSR pendiente' : 'CSR pending'}</dt><dd>{formatCurrency(csrPending)}</dd><span>{csrPending > 0 ? (es ? 'Por cobrar' : 'To collect') : (es ? 'Al día' : 'Up to date')}</span></div>}
            </dl>
            <div className="cdx-actions">
              {openClaim}
              <button type="button" className="cdx-collapse" aria-expanded="true" onClick={headerToggle}>
                {es ? 'Contraer' : 'Collapse'} <i className="fa-solid fa-chevron-up" aria-hidden="true" />
              </button>
            </div>
          </header>

          {sections.length > 0 && (
            <div className="cdx-sections">
              {sections.map(section => (
                <section key={section.title} className="cdx-section">
                  <h4><i className={section.icon} aria-hidden="true" /> {section.title}</h4>
                  <dl>
                    {section.rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
                  </dl>
                </section>
              ))}
            </div>
          )}

          <h4 className="cdx-checks-title"><i className="fa-solid fa-money-check-dollar" aria-hidden="true" /> {es ? 'Cheques' : 'Checks'} <span>{checks.length}</span></h4>
          <div className="cdx-checks">{checks.map(renderCheckBreakdown)}</div>
          {/* Copia abajo para no tener que subir a contraer. */}
          <div className="cdx-foot">
            {openClaim}
            <button type="button" className="cdx-collapse" aria-expanded="true" onClick={headerToggle}>
              {es ? 'Contraer' : 'Collapse'} <i className="fa-solid fa-chevron-up" aria-hidden="true" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div key={groupKey} data-group-key={groupKey} className="cd-claim-card cd-claim-group">
        <div className="cd-claim-card-header" role="button" tabIndex={0} aria-expanded={false} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggleExpand(groupKey); } }} onClick={headerToggle}>
          <strong><i className="fa-solid fa-hashtag"></i> {primary.claimNumber || '—'}</strong>
          <span className="cd-card-header-right">
            {isSingle
              ? <span className={`cd-status-badge ${checkStage(primary, language).className}`}>{checkStage(primary, language).label}</span>
              : <span className="cd-status-badge cd-status-count">{checks.length} {es ? 'cheques' : 'checks'}</span>}
            <i className="fa-solid fa-chevron-down cd-card-chevron"></i>
          </span>
        </div>
        <div className="cd-claim-summary" onClick={headerToggle}>
          <div className="cd-claim-row"><span className="cd-label"><i className="fa-solid fa-route"></i> {es ? 'Paso' : 'Step'}</span><span className="cd-value">{stepLabel}</span></div>
          <div className="cd-claim-row"><span className="cd-label"><i className="fa-solid fa-user"></i> {es ? 'Cliente' : 'Client'}</span><span className="cd-value">{primary.clientName || 'N/A'}</span></div>
          {options.showCompany && <div className="cd-claim-row"><span className="cd-label"><i className="fa-solid fa-building"></i> {es ? 'Empresa' : 'Company'}</span><span className="cd-value">{primary.companyName || 'N/A'}</span></div>}
          {options.showAdjuster && <div className="cd-claim-row"><span className="cd-label"><i className="fa-solid fa-user-tie"></i> {es ? 'Ajustador' : 'Adjuster'}</span><span className="cd-value">{primary.adjusterName || 'N/A'}</span></div>}
          <div className="cd-claim-row"><span className="cd-label"><i className="fa-solid fa-shield-halved"></i> {es ? 'Aseguradora' : 'Insurance Company'}</span><span className="cd-value">{primary.insuranceCompanyName || 'N/A'}</span></div>
          <div className="cd-claim-row cd-highlight"><span className="cd-label"><i className="fa-solid fa-money-check-dollar"></i> {isSingle ? (es ? 'Cheque' : 'Check') : (es ? 'Total cheques' : 'Total checks')}</span><span className="cd-value">{formatCurrency(totalAmount)}</span></div>
          <div className="cd-claim-row"><span className="cd-label"><i className="fa-solid fa-coins"></i> {isSingle ? (es ? 'Ganancia' : 'Earning') : (es ? 'Ganancia total' : 'Total earning')}</span><span className="cd-value">{formatCurrency(totalEarning)}</span></div>
        </div>
        <div className="cd-card-footer-group">
          <div className="cd-preview-footer cd-preview-footer-plain">
            <span>{checks.length} {checks.length === 1 ? (es ? 'cheque' : 'check') : (es ? 'cheques' : 'checks')}</span>
          </div>
          <button type="button" className="cd-btn cd-btn-view-detail" aria-expanded={false} onClick={event => { event.stopPropagation(); headerToggle(); }}>
            {es ? 'Expandir' : 'Expand'}
            <i className="fa-solid fa-chevron-down" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  };

  const totalAmount = filteredClaims.reduce((sum, c) => sum + (c.amount || 0), 0);
  // Comisión bruta del ajustador: su % sobre el monto del cheque, antes de que CSR tome su corte.
  const totalAdjusterGross = filteredClaims.reduce((sum, c) => sum + ((c.amount || 0) * (c.adjusterPercentage || 0)) / 100, 0);
  // c.totalCollected ya es el monto de CSR (su % sobre la comisión bruta del ajustador) — ver claimService.js.
  const totalCsrAmount = filteredClaims.reduce((sum, c) => sum + (c.totalCollected || 0), 0);
  // Lo que le queda al ajustador después del corte de CSR.
  const totalAdjusterNet = totalAdjusterGross - totalCsrAmount;
  // Parte del monto de CSR que aún no se cobró.
  const totalCsrPending = filteredClaims.reduce((sum, c) => sum + (c.pending || 0), 0);


  // Desglose completo de un cheque dentro de su reclamo (tarjeta expandida):
  // monto, comisión del PA, corte de CSR, neto del PA y el cobro de CSR.
  const renderCheckBreakdown = check => {
    const amount = Number(check.amount) || 0;
    const paPct = Number(check.adjusterPercentage) || 0;
    const paGross = (amount * paPct) / 100;
    const csr = Number(check.totalCollected) || 0;
    const stage = checkStage(check, language);
    const es = language === 'es';
    const cells = [
      [es ? 'Cheque' : 'Check', formatCurrency(amount)],
      [es ? `PA (${paPct}%)` : `PA (${paPct}%)`, formatCurrency(paGross)],
      showCsrAmounts && [es ? `CSR (${check.csrPercentage ?? 10}% del PA)` : `CSR (${check.csrPercentage ?? 10}% of PA)`, formatCurrency(csr)],
      [es ? 'Neto del PA' : 'PA net', formatCurrency(paGross - csr), 'is-key'],
      showCsrAmounts && [es ? 'Cobrado a CSR' : 'Collected by CSR', formatCurrency(check.deposit || 0)],
      showCsrAmounts && [es ? 'Pendiente CSR' : 'CSR pending', formatCurrency(check.pending || 0)]
    ].filter(Boolean);
    return (
      // Cobrado (comisión ya cobrada, paso final) en verde; el resto, pendiente en ámbar.
      <div key={check.id} className={`cd-check-breakdown ${stage.className === 'cd-status-paid' ? 'is-paid' : 'is-pending'}`}>
        <div className="cd-check-breakdown-head">
          {showCsrAmounts && <span className="cd-preview-number">{check.invoiceNumber ? `#${String(check.invoiceNumber).replace(/^#/, '').padStart(3, '0')}` : '—'}</span>}
          <span className={`cd-status-badge ${stage.className}`}>{stage.label}</span>
          {showCsrAmounts && paymentBackupStatus(check) === 'pending_backup' && <span className="cd-status-badge cd-status-pending">{PAYMENT_BACKUP_LABELS.pending_backup[es ? 'es' : 'en']}</span>}
          <span className="cd-check-breakdown-meta">
            {[check.checkNumber && `${es ? 'Cheque' : 'Check'} #${check.checkNumber}`, check.paymentType, check.checkDate && formatDateToMMDDYYYY(check.checkDate)].filter(Boolean).join(' · ')}
          </span>
        </div>
        <dl className="cd-check-breakdown-grid">
          {cells.map(([label, value, tone]) => (
            <div key={label} className={tone || ''}><dt>{label}</dt><dd>{value}</dd></div>
          ))}
        </dl>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="cd-loading">
        <div className="spinner"></div>
        <p>{language === 'es' ? 'Cargando reclamos...' : 'Loading claims...'}</p>
      </div>
    );
  }

  const compactMoney = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(value || 0);
  // Tarjetas de P.A para Comisiones (mismas que en Reclamos): filtran por ajustador.
  const paCardsFor = title => (
    <PaCards
      title={title}
      clearLabel={language === 'es' ? 'Ver todos' : 'Show all'}
      ariaLabel={language === 'es' ? 'Filtrar por ajustador' : 'Filter by adjuster'}
      selected={adjusterFilter}
      onSelect={name => setAdjusterFilter(lockedAdjuster || name)}
      items={adjustersInView.map(({ name, count, claimCount, amount }) => ({
        name, profile: profileByName.get(normName(name)), active: false,
        stats: [
          { label: language === 'es' ? 'Reclamos' : 'Claims', value: claimCount },
          { label: language === 'es' ? 'Cheques' : 'Checks', value: count },
          // El dinero del Super Admin vive en el Resumen.
          !isAdmin && { label: language === 'es' ? 'Monto' : 'Amount', value: compactMoney(amount) }
        ].filter(Boolean)
      }))}
    />
  );

  // Tarjetas de compañía (Super Admin): cifras de cada una; la elegida filtra
  // toda la pantalla (mismo filtro que el selector de arriba) y se abre con
  // las tarjetas de sus ajustadores.
  const companyStats = (() => {
    const map = new Map();
    allChecks.forEach(check => {
      const name = check.companyName || (language === 'es' ? 'Sin empresa' : 'No company');
      const entry = map.get(name) || { name, checks: 0, claims: new Set(), adjusters: new Set() };
      entry.checks += 1;
      entry.claims.add(check.claimRecordId || check.claimNumber || check.id);
      if (check.adjusterName) entry.adjusters.add(check.adjusterName);
      map.set(name, entry);
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  })();
  const companyCards = (
    <div className="co-cards">
      <div className="crl-pa-head"><h3>{language === 'es' ? 'Compañías' : 'Companies'}</h3>
        {companyScope !== 'all' && <button type="button" className="crl-pa-clear" onClick={() => setCompanyScope('all')}>{language === 'es' ? 'Ver todas' : 'Show all'}</button>}
      </div>
      <div className="co-grid" role="group" aria-label={language === 'es' ? 'Filtrar por compañía' : 'Filter by company'}>
        {companyStats.map(company => {
          const selected = companyScope === company.name;
          return (
            <button key={company.name} type="button" className={`co-card ${selected ? 'is-selected' : ''}`} aria-pressed={selected} aria-expanded={selected}
              onClick={() => setCompanyScope(selected ? 'all' : company.name)}>
              <span className="co-card-logo" aria-hidden="true"><CompanyLogos company={company.name} /></span>
              <span className="co-card-text"><strong>{company.name}</strong><small>{language === 'es' ? `${company.checks} cheque(s) · ${company.claims.size} reclamo(s) · ${company.adjusters.size} ajustador(es)` : `${company.checks} check(s) · ${company.claims.size} claim(s) · ${company.adjusters.size} adjuster(s)`}</small></span>
              <i className={`fa-solid fa-chevron-${selected ? 'up' : 'down'} co-card-caret`} aria-hidden="true" />
            </button>
          );
        })}
      </div>
      {companyScope !== 'all' && adjustersInView.length > 0 && (
        <div className="co-expand">{paCardsFor(language === 'es' ? `Ajustadores de ${companyScope}` : `${companyScope} adjusters`)}</div>
      )}
    </div>
  );


  return (
    <div className="cd-container">
      {/* En Comisiones del Super Admin, las tarjetas de compañía hacen de selector. */}
      {showCompanyPicker && !(directoryTab === 'commissions' && alwaysPaCards && isAdmin) && (
        <div className="cd-company-picker" role="group" aria-label={language === 'es' ? 'Compañía' : 'Company'}>
          <span className="cd-company-picker-label"><i className="fa-solid fa-building" aria-hidden="true"></i> {language === 'es' ? 'Compañía' : 'Company'}</span>
          <div className="cd-view-toggle">
            <button type="button" className={`cd-view-btn ${companyScope === 'all' ? 'active' : ''}`} aria-pressed={companyScope === 'all'} onClick={() => setCompanyScope('all')}>
              {language === 'es' ? 'Todas' : 'All'} <span className="cd-company-count">{allClaimRecords.length}</span>
            </button>
            {companyOptions.map(option => (
              <button key={option.name} type="button" className={`cd-view-btn ${companyScope === option.name ? 'active' : ''}`} aria-pressed={companyScope === option.name} onClick={() => setCompanyScope(option.name)}>
                {option.name} <span className="cd-company-count">{option.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Lo que necesita atención: tarjetas resumidas y, al tocar una, su lista
          a todo el ancho (AttentionBoard). En Reclamos van plazos, decisiones,
          detenidos y RD; en Comisiones, los pagos sin comprobante. */}
      <AttentionBoard
        key={directoryTab}
        es={language === 'es'}
        ariaLabel={language === 'es' ? 'Necesita atención' : 'Needs attention'}
        defaultOpen={directoryTab === 'claims'
          ? (deadlineAlerts.some(alert => alert.timer.status === 'overdue') ? 'deadlines' : !csrTeam && awaitingPa.length > 0 ? 'decisions' : null)
          : null}
        sections={directoryTab === 'claims' ? [
          {
            id: 'deadlines', icon: 'fa-solid fa-stopwatch', label: language === 'es' ? 'Plazos que vencen' : 'Deadlines',
            count: deadlineAlerts.length,
            tone: deadlineAlerts.some(alert => alert.timer.status === 'overdue') ? 'danger' : 'warning',
            hint: (() => {
              const overdue = deadlineAlerts.filter(alert => alert.timer.status === 'overdue').length;
              if (overdue) return language === 'es' ? `${overdue} vencido(s)` : `${overdue} overdue`;
              return deadlineAlerts[0] ? `${language === 'es' ? 'Próximo' : 'Next'}: ${deadlineAlerts[0].name || 'N/A'}` : '';
            })(),
            items: deadlineAlerts.map(alert => ({
              key: alert.key, number: alert.number ? `#${alert.number}` : '', title: alert.name, detail: alert.what,
              note: alert.timer.message ? (language === 'es' ? alert.timer.message[0] : alert.timer.message[1]) : '',
              badge: <TimerBadge timer={alert.timer} es={language === 'es'} />,
              to: `/claim-status/claim/${alert.claimRecordId}`, external: true, action: language === 'es' ? 'Abrir reclamo' : 'Open claim'
            }))
          },
          {
            id: 'decisions', icon: 'fa-solid fa-gavel',
            label: csrTeam ? (language === 'es' ? 'Esperando decisión del PA' : 'Waiting on PA decision') : (language === 'es' ? 'Tus decisiones pendientes' : 'Your pending decisions'),
            count: awaitingPa.length, tone: 'warning',
            hint: awaitingPa.length ? (language === 'es' ? `La más antigua: ${Math.max(0, ...awaitingPa.map(item => daysIn(item) || 0))} día(s)` : `Oldest: ${Math.max(0, ...awaitingPa.map(item => daysIn(item) || 0))} day(s)`) : '',
            items: awaitingPa.map(item => ({
              key: item.key, number: item.claimNumber ? `#${item.claimNumber}` : '', title: item.clientName,
              detail: [item.adjusterName, daysIn(item) !== null ? `${daysIn(item)} ${language === 'es' ? 'día(s) esperando' : 'day(s) waiting'}` : ''].filter(Boolean).join(' · '),
              badge: <span className="cd-status-badge cd-status-pending">{item.what}</span>,
              to: `/claim-status/claim/${item.claimRecordId}`, external: true, action: language === 'es' ? 'Decidir' : 'Decide'
            }))
          },
          {
            id: 'stuck', icon: 'fa-solid fa-hourglass-half',
            label: language === 'es' ? `Detenidos (${STUCK_DAYS}+ días en el paso)` : `Stalled (${STUCK_DAYS}+ days in step)`,
            count: stuckClaims.length, tone: 'neutral',
            hint: stuckClaims[0] ? (language === 'es' ? `El más largo: ${stuckClaims[0].days} días` : `Longest: ${stuckClaims[0].days} days`) : '',
            items: stuckClaims.map(({ cr, days }) => ({
              key: cr.id, number: cr.claimNumber ? `#${cr.claimNumber}` : '', title: cr.clientName,
              detail: `${claimStepLabel(cr, language)}${cr.adjusterName && !lockedAdjuster ? ` · ${cr.adjusterName}` : ''}`,
              badge: <span className={`cd-stuck-days ${days >= STUCK_DAYS * 2 ? 'is-critical' : ''}`}>{days} {language === 'es' ? 'días' : 'days'}</span>,
              to: `/claim-status/claim/${cr.id}`, state: { from: `${location.pathname}${location.search}` }, action: language === 'es' ? 'Abrir reclamo' : 'Open claim'
            }))
          },
          {
            id: 'rd', icon: 'fa-solid fa-rotate-left', label: language === 'es' ? 'Depreciación recuperable por pedir' : 'Recoverable Depreciation to request',
            count: rdDue.length, tone: 'info',
            hint: rdDue.length ? formatCurrency(rdDue.reduce((total, item) => total + (Number(item.amount) || 0), 0)) : '',
            items: rdDue.map(item => ({
              key: item.claimRecordId, number: item.claimNumber ? `#${item.claimNumber}` : '', title: item.clientName,
              detail: `${language === 'es' ? 'El cliente ya cobró: envía las pruebas al seguro y pide la RD.' : 'The client has been paid: send the proof and request the RD.'}${item.adjusterName && !lockedAdjuster ? ` · ${item.adjusterName}` : ''}`,
              badge: <span className="cd-status-badge cd-status-pending">RD {formatCurrency(item.amount)}</span>,
              to: `/claim-status/claim/${item.claimRecordId}?section=claim-details`, state: { from: `${location.pathname}${location.search}` }, action: language === 'es' ? 'Abrir reclamo' : 'Open claim'
            }))
          }
        ] : [
          {
            id: 'backlog', icon: 'fa-solid fa-receipt', label: language === 'es' ? 'Pagos sin backup (BofA / Zelle)' : 'Payments without backup (BofA / Zelle)',
            count: paymentBacklog.length, tone: 'warning',
            hint: paymentBacklog.length ? `${formatCurrency(paymentBacklog.reduce((total, { check }) => total + (Number(check.totalCollected) || 0), 0))} ${language === 'es' ? 'sin comprobante' : 'without receipt'}` : '',
            items: paymentBacklog.map(({ check, status }) => ({
              key: check.id, number: check.invoiceNumber ? `#${String(check.invoiceNumber).replace(/^#/, '').padStart(3, '0')}` : '',
              title: check.clientName, detail: [check.claimNumber, check.adjusterName, formatCurrency(check.totalCollected || 0)].filter(Boolean).join(' · '),
              badge: <span className="cd-status-badge cd-status-pending">{PAYMENT_BACKUP_LABELS[status][language === 'es' ? 'es' : 'en']}</span>,
              to: check.claimRecordId ? `/claim-status/claim/${check.claimRecordId}?section=claim-checks` : null, external: true,
              action: language === 'es' ? 'Subir comprobante' : 'Upload receipt'
            }))
          }
        ]}
      />
      {!view && (
      <div className="cd-view-toggle cd-directory-tabs" role="group" aria-label={language === 'es' ? 'Vista de la pantalla' : 'Screen view'}>
        <button type="button" className={`cd-view-btn ${directoryTab === 'claims' ? 'active' : ''}`} aria-pressed={directoryTab === 'claims'} onClick={() => setDirectoryTab('claims')}>
          <i className="fa-solid fa-folder-open" aria-hidden="true"></i> {language === 'es' ? 'Reclamos' : 'Claims'}
        </button>
        {showCommissions && (
          <button type="button" className={`cd-view-btn ${directoryTab === 'commissions' ? 'active' : ''}`} aria-pressed={directoryTab === 'commissions'} onClick={() => setDirectoryTab('commissions')}>
            <i className="fa-solid fa-coins" aria-hidden="true"></i> {language === 'es' ? 'Comisiones por cheque' : 'Check commissions'}
          </button>
        )}
      </div>
      )}

      {directoryTab === 'claims' && (
        recordsLoaded
          ? <ClaimRecordList claimRecords={claimRecords} adjusters={adjusters} checks={claims} language={language} isAdmin={isAdmin} lockedCompany={lockedCompany} companyPicker={!showCompanyPicker} lockedAdjuster={lockedAdjuster} adjustersAlwaysOpen={userRoleKey === 'pa_admin'} />
          : <div className="cd-loading"><div className="spinner"></div></div>
      )}

      {directoryTab === 'commissions' && (
      <>
      {/* El Super Admin ve totales y ganancias por compañía en el Resumen;
          aquí solo revisa los cheques. Los demás roles conservan sus totales. */}
      {!isAdmin && <div className="cd-summary-grid">
        <div className="cd-summary-card">
          <div className="cd-summary-label">
            <i className="fa-solid fa-receipt"></i>
            {language === 'es' ? 'Reclamos' : 'Claims'}
          </div>
          <div className="cd-summary-value">{claimGroups.length}</div>
          <div className="cd-summary-sub">
            <span className="cd-summary-sub-label">{language === 'es' ? 'Cheques' : 'Checks'}</span>
            <span className="cd-summary-sub-value">{filteredClaims.length}</span>
          </div>
        </div>
        <div className="cd-summary-card">
          <div className="cd-summary-label">
            <i className="fa-solid fa-dollar-sign"></i>
            {language === 'es' ? 'Monto Total de Cheques' : 'Total Check Amount'}
          </div>
          <div className="cd-summary-value">{formatCurrency(totalAmount)}</div>
          <div className="cd-summary-sub">
            <span className="cd-summary-sub-label">{language === 'es' ? 'A cobrar' : 'To collect'}</span>
            <span className="cd-summary-sub-value">{formatCurrency(totalAdjusterGross)}</span>
          </div>
        </div>
        <div className="cd-summary-card">
          <div className="cd-summary-label">
            <i className="fa-solid fa-user-tie"></i>
            {language === 'es' ? 'Ganancia Ajustador' : 'Adjuster Earnings'}
          </div>
          <div className="cd-summary-value">{formatCurrency(totalAdjusterNet)}</div>
          <div className="cd-summary-sub">
            <span className="cd-summary-sub-label">{showCsrAmounts ? (language === 'es' ? 'Bruto − CSR' : 'Gross − CSR') : (language === 'es' ? 'Bruto' : 'Gross')}</span>
            <span className="cd-summary-sub-value">{showCsrAmounts ? `${formatCurrency(totalAdjusterGross)} − ${formatCurrency(totalCsrAmount)}` : formatCurrency(totalAdjusterGross)}</span>
          </div>
        </div>
        {showCsrAmounts && (
          <div className="cd-summary-card">
            <div className="cd-summary-label">
              <i className="fa-solid fa-coins"></i>
              {language === 'es' ? 'Monto CSR' : 'CSR Amount'}
            </div>
            <div className="cd-summary-value">{formatCurrency(totalCsrAmount)}</div>
            <div className="cd-summary-sub">
              <span className="cd-summary-sub-label">{language === 'es' ? 'Pendiente' : 'Pending'}</span>
              <span className="cd-summary-sub-value cd-summary-sub-pending">{formatCurrency(totalCsrPending)}</span>
            </div>
          </div>
        )}
      </div>}

      <section className="cd-controls crl-controls" aria-label={language === 'es' ? 'Buscar y organizar reclamos' : 'Find and organize claims'}>
        <h2 className="sr-only">{language === 'es' ? 'Comisiones por cheque' : 'Check commissions'}</h2>
        <div className="crl-bar">
          <div className="cd-filter-search">
            <i className="fa-solid fa-search" aria-hidden="true"></i>
            <input
              type="search"
              aria-label={language === 'es' ? 'Buscar cliente, reclamo o factura' : 'Search client, claim or invoice'}
              placeholder={language === 'es' ? 'Buscar cliente, reclamo o factura…' : 'Search client, claim or invoice…'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button type="button" className={`crl-filter-toggle ${commissionFiltersOpen ? 'is-open' : ''}`} aria-expanded={commissionFiltersOpen} aria-controls="cd-filter-panel" onClick={() => setCommissionFiltersOpen(open => !open)}>
            <i className="fa-solid fa-sliders" aria-hidden="true" /> {language === 'es' ? 'Filtros y orden' : 'Filters & sort'}{activeFilters.length > 0 && <span className="crl-filter-count">{activeFilters.length}</span>}
          </button>
          {!alwaysPaCards && <div className="crl-toolbar">
            <div className="cd-view-toggle" role="group" aria-label={language === 'es' ? 'Vista de reclamos' : 'Claims view'}>
              <button type="button" className={`cd-view-btn ${viewMode === 'all' ? 'active' : ''}`} aria-pressed={viewMode === 'all'} onClick={() => setViewMode('all')}>
                <i className="fa-solid fa-table-list" aria-hidden="true"></i> {language === 'es' ? 'Todos' : 'All'}
              </button>
              <button type="button" className={`cd-view-btn ${viewMode === 'grouped' ? 'active' : ''}`} aria-pressed={viewMode === 'grouped'} onClick={() => setViewMode('grouped')}>
                <i className="fa-solid fa-layer-group" aria-hidden="true"></i> {language === 'es' ? 'Por ajustador' : 'By adjuster'}
              </button>
            </div>
          </div>}
        </div>
        {commissionFiltersOpen && (
          <div className="crl-filter-panel" id="cd-filter-panel">
            <div className="crl-filters">
          <div className="cd-select-wrap">
            <i className="fa-solid fa-route"></i>
            <select aria-label={language === 'es' ? 'Paso del reclamo' : 'Claim step'} value={stepFilter} onChange={(e) => setStepFilter(e.target.value)}>
              <option value="all">{language === 'es' ? 'Todos los pasos' : 'All steps'}</option>
              {CLAIM_PHASES.map(phase => (
                <optgroup key={phase.id} label={`${phase.id}. ${language === 'es' ? phase.label.es : phase.label.en}`}>
                  <option value={`phase:${phase.id}`}>{language === 'es' ? `Toda la fase ${phase.id}` : `All of phase ${phase.id}`}</option>
                  {CLAIM_FLOW.steps.filter(st => st.phase === phase.id).map(st => <option key={st.id} value={st.id}>{language === 'es' ? st.label.es : st.label.en}</option>)}
                </optgroup>
              ))}
              <optgroup label={language === 'es' ? 'Cierre' : 'Closed'}>
                {CLAIM_FLOW.steps.filter(st => st.phase === 'end').map(st => <option key={st.id} value={st.id}>{language === 'es' ? st.label.es : st.label.en}</option>)}
              </optgroup>
              <option value="none">{language === 'es' ? 'Sin paso asignado' : 'No step'}</option>
            </select>
            <i className="fa-solid fa-chevron-down cd-select-caret"></i>
          </div>
          <div className="cd-select-wrap">
            <i className="fa-solid fa-flag"></i>
            <select aria-label={language === 'es' ? 'Paso del cheque' : 'Check step'} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_FILTER_OPTIONS.map(opt => (
                <option key={opt.key} value={opt.key}>{language === 'es' ? opt.es : opt.en}</option>
              ))}
            </select>
            <i className="fa-solid fa-chevron-down cd-select-caret"></i>
          </div>
              <div className="cd-select-wrap">
                <i className="fa-solid fa-arrow-down-wide-short" aria-hidden="true"></i>

              <select aria-label={language === 'es' ? 'Ordenar por' : 'Sort by'} value={sortField} onChange={(e) => setSortField(e.target.value)}>
                <option value="checkDate">{language === 'es' ? 'Fecha Cheque' : 'Check Date'}</option>
                <option value="invoiceNumber">{language === 'es' ? 'N.º de creación' : 'Creation number'}</option>
                <option value="clientName">{language === 'es' ? 'Cliente' : 'Client'}</option>
                <option value="amount">{language === 'es' ? 'Monto' : 'Amount'}</option>
                {showCsrAmounts && <option value="totalCollected">{language === 'es' ? 'A Cobrar' : 'To Collect'}</option>}
                {showCsrAmounts && <option value="pending">{language === 'es' ? 'Pendiente' : 'Pending'}</option>}
              </select>
              <i className="fa-solid fa-chevron-down cd-select-caret"></i>

              </div>
            <button
              className="cd-sort-direction-btn"
              onClick={() => setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'))}
              aria-label={sortDirection === 'asc' ? (language === 'es' ? 'Cambiar a descendente' : 'Sort descending') : (language === 'es' ? 'Cambiar a ascendente' : 'Sort ascending')}
              title={sortDirection === 'asc' ? (language === 'es' ? 'Ascendente' : 'Ascending') : (language === 'es' ? 'Descendente' : 'Descending')}
            >
              <i className={`fa-solid ${sortDirection === 'asc' ? 'fa-arrow-up-short-wide' : 'fa-arrow-down-wide-short'}`}></i>
            </button>

            </div>
          </div>
        )}
        <div className="cd-filter-feedback">
          <div className="cd-active-filters" aria-label={language === 'es' ? 'Filtros activos' : 'Active filters'}>
            <span className="crl-count" aria-live="polite">{claimGroups.length} {language === 'es' ? 'reclamo(s)' : 'claim(s)'}</span>
            <span className="crl-range-inline">{filteredClaims.length} {language === 'es' ? 'cheque(s)' : 'check(s)'}</span>
            {activeFilters.map((filter, index) => <button type="button" className="cd-filter-chip" key={index} onClick={filter.clear} aria-label={`${language === 'es' ? 'Quitar filtro' : 'Remove filter'}: ${filter.label}`}><span>{filter.label}</span><i className="fa-solid fa-xmark" aria-hidden="true" /></button>)}
          </div>
          <button type="button" className="cd-clear-filters" disabled={!activeFilters.length} onClick={clearFilters}><i className="fa-solid fa-rotate-left" aria-hidden="true" />{language === 'es' ? 'Limpiar filtros' : 'Clear filters'}</button>
        </div>
      </section>

      {error && (
        <div className="cd-error">
          <i className="fa-solid fa-exclamation-triangle"></i> {error}
        </div>
      )}

      {!error && filteredClaims.length === 0 && (
        <div className="cd-no-results">
          <i className="fa-solid fa-folder-open"></i>
          <p>{language === 'es' ? 'No hay reclamos que coincidan con los filtros' : 'No claims match the current filters'}</p>
        </div>
      )}

      {/* Super Admin con varias compañías: una tarjeta por compañía que al
          tocarla filtra la pantalla y se abre con sus ajustadores. Si no,
          tarjetas de P.A directas (P.A Admin o una sola compañía). */}
      {alwaysPaCards && isAdmin && showCompanyPicker ? companyCards
        : alwaysPaCards && adjustersInView.length > 0 && paCardsFor(paAdmin ? (language === 'es' ? 'Tus P.A' : 'Your P.As') : (language === 'es' ? 'Ajustadores' : 'Adjusters'))}

      {viewMode === 'grouped' && (
        <>
          {paCardsFor(null)}

          {adjusterFilter === 'all' ? (
            <div className="cd-no-results">
              <i className="fa-solid fa-hand-pointer"></i>
              <p>{language === 'es' ? 'Selecciona un ajustador para ver sus reclamos' : 'Select an adjuster to see their claims'}</p>
            </div>
          ) : (
            <div className="cd-claims-grid">
              {claimGroups.map(checks => renderClaimGroup(checks, { showCompany: !lockedCompany }))}
            </div>
          )}
        </>
      )}

      {viewMode === 'all' && filteredClaims.length > 0 && (
        <>


          <div className="cd-claims-grid">
            {claimGroups.map(checks => renderClaimGroup(checks, { showCompany: !lockedCompany, showAdjuster: true }))}
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
};

export default ClaimsDirectory;
